import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';

import { KEYBOARD } from '../../../config/constants';
import { hasSlotContent, isTouchDevice } from '../../../services/utils';

/**
 * A menu displays a list of choices on a temporary surface. They appear when users interact with a button, action, or other control.
 *
 * @part footer - HTML `<div>` container that holds the footer content inside the component's shadow DOM
 * @part footer-divider - HTML `<pk-divider>` above the footer container
 * @part header - HTML `<div>` container that holds the header content inside the component's shadow DOM
 * @part header-divider - HTML `<pk-divider>` below the header container
 * @part main - HTML `<div>` container that holds the menu items inside the component's shadow DOM
 * @part menu - HTML `<div>` root container inside the component's shadow DOM
 */
@Component({
  tag: 'pk-menu',
  styleUrl: './scss/pk-menu.scss',
  shadow: true,
})
export class PkMenu {
  // Own Properties
  // ====================

  private MENU_ITEM_SELECTOR = 'pk-menu-item';
  private MENU_SUBITEM_SELECTOR = 'pk-menu-subitem';

  private pkMenu: HTMLDivElement;
  private pkMenuHeader: HTMLDivElement;
  private pkMenuFooter: HTMLDivElement;
  private ignoreMouseEvents: boolean = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private ignoreMouseTimeout: any;

  // Reference to host HTML element
  // ===================================

  @Element() el: HTMLPkMenuElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() hasFocus: boolean = false;
  @State() hasHeader: boolean = false;
  @State() hasFooter: boolean = false;

  // Public Property API
  // ========================

  /** A human-readable string value which identifies the menu list */
  @Prop({ reflect: true }) ariaLabel: string | null;

  /** If true, the border left of the menu item will be highlighted when selected */
  @Prop({ reflect: true }) highlight: boolean = false;

  /** If true, the  header and footer dividers won't be shown */
  @Prop({ reflect: true }) hideSectionDivider: boolean = false;

  /** If true, the divider between menu items won't be shown */
  @Prop({ reflect: true }) hideItemsDivider: boolean = false;

  /** If true, multiple menu items with submenu items (`hasSubmenu="true"`) can be expanded */
  @Prop({ reflect: true }) multiExpand: boolean = false;

  /** Set the role attribute */
  @Prop({ reflect: true }) role: 'menu' | 'menuitem' = 'menu';

  // Prop lifecycle events
  // =======================

  @Watch('highlight')
  handleHighlight() {
    const menuItems = this.getItems();
    if (!menuItems.length) return;

    menuItems.forEach((item: HTMLPkMenuItemElement) => (item.highlight = this.highlight));
  }

  @Watch('hideItemsDivider')
  handleItemDividers() {
    const menuItems = this.getItems();
    if (!menuItems.length) return;

    menuItems.forEach((item: HTMLPkMenuItemElement) => {
      // Skip the last item, it have no border anyway
      if (item === menuItems.at(-1)) return;

      if (!this.hideItemsDivider) {
        return item.style.removeProperty('--pk-menu-item--border-bottom');
      }

      item.style.setProperty('--pk-menu-item--border-bottom', 'none');
    });
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Emitted when the menu gains focus. */
  @Event() pkFocus: EventEmitter;

  /** Emitted when the menu loses focus. */
  @Event() pkBlur: EventEmitter;

  /** Emitted when a menu item is selected. */
  @Event() pkSelect: EventEmitter;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentDidLoad() {
    this.removeLastItemDivider();
    this.handleItemDividers();
    this.handleHighlight();
  }

  // Listeners
  // ==============

  @Listen('pkFocus')
  handleFocusEvent(e: CustomEvent) {
    const item = this.getItems().find((i) => i === e.target);
    if (!item) return;

    this.setActiveItem(item);
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  /** Sets focus on the menu. */
  @Method()
  async setFocus() {
    this.hasFocus = true;
    this.pkMenu.focus();
  }

  /** Removes focus from the menu. */
  @Method()
  async removeFocus() {
    this.hasFocus = false;
    this.pkMenu.blur();
  }

  /** Collapse all menu items that have submenu items */
  @Method()
  async collapseAll(): Promise<void> {
    const menuItems = this.getItems();
    if (!menuItems.length) return;

    menuItems.forEach((item: HTMLPkMenuItemElement) => {
      if (!item.hasSubmenu) return;
      item.expanded = item.selected ?? false;
    });
  }

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private getItems = (): HTMLPkMenuItemElement[] => {
    if (!this.pkMenu) return [];

    const slot = this.pkMenu.querySelector('slot');
    return [...slot.assignedElements({ flatten: true })].filter(
      (el: HTMLPkMenuItemElement) => el.tagName.toLowerCase() === this.MENU_ITEM_SELECTOR && !el.disabled,
    ) as HTMLPkMenuItemElement[];
  };

  private getActiveItem = () => {
    return this.getItems().find((i) => i.active);
  };

  private setActiveItem = (item?: HTMLPkMenuItemElement) => {
    this.getItems().forEach((i) => (i.active = i === item));
  };

  private removeLastItemDivider = () => {
    const menuItems = this.getItems();
    if (!menuItems.length) return;

    menuItems.at(-1).style.setProperty('--menu-item--border-bottom', 'none');
  };

  private handleHeaderSlotChange = () => {
    if (!this.pkMenuHeader) return;

    this.hasHeader = hasSlotContent(this.pkMenuHeader);
  };

  private handleFooterSlotChange = () => {
    if (!this.pkMenuFooter) return;

    this.hasFooter = hasSlotContent(this.pkMenuFooter);
  };

  private handleClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const item: HTMLPkMenuItemElement =
      target.closest(this.MENU_SUBITEM_SELECTOR) ?? target.closest(this.MENU_ITEM_SELECTOR);
    if (!item || item.disabled || item.hasSubmenu) return;

    this.pkSelect.emit({ item });
  };

  private handleFocus = () => {
    const item = this.getActiveItem();
    if (!item) {
      this.setActiveItem(this.getItems()[0]);
    }
    this.pkFocus.emit();
  };

  private handleMouseOver = (event: MouseEvent) => {
    if (isTouchDevice()) return;

    const target = event.target as HTMLElement;
    const item: HTMLPkMenuItemElement = target.closest(this.MENU_ITEM_SELECTOR);
    if (!item || this.ignoreMouseEvents) return;

    this.setActiveItem(item);
  };

  private handleMouseOut = (event: MouseEvent) => {
    if (isTouchDevice()) return;

    const target = event.target as HTMLElement;
    const item: HTMLPkMenuItemElement = target.closest(this.MENU_ITEM_SELECTOR);
    if (!item || this.ignoreMouseEvents) return;

    this.setActiveItem(null);
  };

  private handleSelectionWhenPressingEnter = (event: KeyboardEvent) => {
    if (event.key !== KEYBOARD.Enter) return;

    event.preventDefault();
    const item = this.getActiveItem();
    if (!item || item.hasSubmenu) return;

    this.pkSelect.emit({ item });
  };

  private preventScrollingWhenPressingSpace = (event: KeyboardEvent) => {
    if (event.key !== KEYBOARD.Space) return;
    event.preventDefault();
  };

  private handleMoveSelection = (event: KeyboardEvent) => {
    const items = this.getItems();
    if (!items.length || ![KEYBOARD.ArrowDown, KEYBOARD.ArrowUp, KEYBOARD.Tab, 'Home', 'End'].includes(event.key))
      return;

    const selectedItem = this.getActiveItem();
    const isTabKey = event.key === KEYBOARD.Tab;
    let index = items.indexOf(selectedItem);
    const lastIndex = items.length - 1;

    const keyActionsMap = {
      [KEYBOARD.ArrowDown]: (i: number) => i + 1,
      [KEYBOARD.ArrowUp]: (i: number) => i - 1,
      Home: () => 0,
      End: () => lastIndex,
    };

    if (!isTabKey) {
      // allow tab to move focus between items
      event.preventDefault();
    }

    const action = keyActionsMap[event.key];
    if (!action) return;

    index = action(index);

    if (index < 0) index = lastIndex;
    if (index > lastIndex) index = 0;

    items[index]?.focusMenuItem();
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    // When keying through the menu, if the mouse happens to be hovering over a menu item and the menu scrolls, the
    // mouseout/mouseover event will fire causing the selection to be different than what the user expects. This gives
    // us a way to temporarily ignore mouse events while the user is interacting with a keyboard.
    clearTimeout(this.ignoreMouseTimeout);
    this.ignoreMouseTimeout = setTimeout(() => (this.ignoreMouseEvents = false), 500);
    this.ignoreMouseEvents = true;
    this.handleSelectionWhenPressingEnter(event);
    this.preventScrollingWhenPressingSpace(event);

    // Move the selection when pressing down or up
    this.handleMoveSelection(event);
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <Host>
        <div
          class="pk-menu"
          onClick={this.handleClick}
          onFocus={this.handleFocus}
          onBlur={this.handleMouseOut}
          onKeyDown={this.handleKeyDown}
          onMouseOver={this.handleMouseOver}
          onMouseOut={this.handleMouseOut}
          aria-label={this.ariaLabel}
          role={this.role}
          part="menu"
          title="menu"
        >
          {/* Menu header content */}
          <div
            class={{ 'pk-menu__header': true, hidden: !this.hasHeader }}
            part="header"
            aria-label={this.ariaLabel}
            aria-current="false"
            role="menuitem"
            ref={(el: HTMLDivElement) => {
              this.pkMenuHeader = el;
              this.handleHeaderSlotChange();
            }}
          >
            <slot name="header" onSlotchange={this.handleHeaderSlotChange} />
          </div>
          <pk-divider class={{ 'pk-menu__divider': true, hidden: this.hideSectionDivider }} part="header-divider" />
          {/* Menu main content */}
          <div class="pk-menu__main" part="main" role="none" ref={(el: HTMLDivElement) => (this.pkMenu = el)}>
            <slot />
          </div>
          {/* Menu footer content */}
          <pk-divider class={{ 'pk-menu__divider': true, hidden: this.hideSectionDivider }} part="footer-divider" />
          <div
            class={{ 'pk-menu__footer': true, hidden: !this.hasFooter }}
            part="footer"
            aria-current="false"
            aria-label="menu footer"
            role="menuitem"
            ref={(el: HTMLDivElement) => {
              this.pkMenuFooter = el;
              this.handleFooterSlotChange();
            }}
          >
            <slot name="footer" onSlotchange={this.handleFooterSlotChange} />
          </div>
        </div>
      </Host>
    );
  }
}
